import React, { useEffect, useRef, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Slider from '@mui/material/Slider';
import Close from '@mui/icons-material/Close';
import VolumeUp from '@mui/icons-material/VolumeUp';
import VolumeDown from '@mui/icons-material/VolumeDown';
import TimerOutlined from '@mui/icons-material/TimerOutlined';
import db from './firebase.config';
import { readRemoteSettings, buildAutoSkipPatch, buildVolumePatch } from './remoteContract';

export function durationLabel(seconds) {
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m${seconds % 60 ? ` ${seconds % 60}s` : ''}`;
}

export default function PopupMenu({ uid, vol_control, isOpen = false, onClose }) {
  const [user, setUser] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [pending, setPending] = useState(false);
  const [volume, setVolume] = useState(50);
  const busy = useRef(false);
  const dragging = useRef(false);
  const settings = readRemoteSettings(user, meta);
  const latestSettings = useRef(settings);
  latestSettings.current = settings;
  const ready = Boolean(isOpen && uid && user && meta && !loadFailed);
  const canChangeVolume = vol_control && user?.config?.enablePaxVolControl !== false;
  const displayedVolume = Math.min(100, Math.max(settings.minVolumePercent, volume));

  useEffect(() => {
    if (!isOpen || !uid) return;
    let active = true;
    dragging.current = false;
    setError(''); setNotice(''); setUser(null); setMeta(null); setLoadFailed(false);
    const failure = () => {
      if (!active) return;
      setLoadFailed(true);
      setError('Couldn’t load playback settings. Close and reopen to retry.');
    };
    const unsubscribeUser = db.collection('Users').doc(uid).onSnapshot(snapshot => {
      if (!active) return;
      if (snapshot.exists) setUser(snapshot.data()); else failure();
    }, failure);
    const unsubscribeMeta = db.collection('Meta').doc('config').onSnapshot(snapshot => {
      if (active) setMeta(snapshot.data() || {});
    }, failure);
    return () => { active = false; unsubscribeUser(); unsubscribeMeta(); };
  }, [uid, isOpen]);

  useEffect(() => {
    if (!dragging.current) setVolume(settings.volumePercent);
  }, [settings.volumePercent, isOpen]);
  useEffect(() => {
    if (!canChangeVolume) { dragging.current = false; setVolume(settings.volumePercent); }
  }, [canChangeVolume, settings.volumePercent]);

  async function save(patch, success) {
    if (!ready || !uid || busy.current) return;
    busy.current = true; setPending(true); setError(''); setNotice('');
    try {
      await db.collection('Users').doc(uid).update(patch);
      setNotice(success);
    } catch {
      setVolume(latestSettings.current.volumePercent);
      setError('Couldn’t save. Please try again.');
    } finally { busy.current = false; setPending(false); }
  }
  function changeDuration(seconds) {
    try { save(buildAutoSkipPatch(true, seconds, settings), 'Auto-skip updated.'); }
    catch { setError('That duration is outside your driver’s settings.'); }
  }
  function commitVolume(value) {
    dragging.current = false;
    if (!canChangeVolume) return;
    try { save(buildVolumePatch(value, settings), 'Volume updated.'); }
    catch { setVolume(settings.volumePercent); setError('Choose a volume within your driver’s limits.'); }
  }
  const presets = [...new Set([settings.minAutoSkipSeconds, 60, 90, 120, settings.maxAutoSkipSeconds, settings.autoSkipSeconds])]
    .filter(value => value >= settings.minAutoSkipSeconds && value <= settings.maxAutoSkipSeconds).sort((a, b) => a - b);

  return <Drawer anchor="bottom" open={isOpen} onClose={onClose} PaperProps={{ className: 'playback-sheet', role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'playback-settings-title', 'aria-describedby': 'playback-settings-description' }}>
    <div className="settings-handle" />
    <div className="sheet-heading"><div><h2 id="playback-settings-title">Playback settings</h2><p id="playback-settings-description">A little adjustment, for the whole ride.</p></div><button className="icon-button" onClick={onClose} aria-label="Close playback settings"><Close /></button></div>
    <section className="settings-section" aria-labelledby="autoskip-title">
      <div className="settings-section-title"><TimerOutlined /><span id="autoskip-title">Auto-skip</span><span className="settings-section-value">{ready ? (settings.autoSkipEnabled ? durationLabel(settings.autoSkipSeconds) : 'Off') : 'Loading…'}</span></div>
      <p className="settings-description">How long each song plays before moving on.</p>
      <div className="skip-presets" aria-label="Auto-skip duration">{presets.map(seconds => <button key={seconds} className={`skip-preset ${settings.autoSkipEnabled && settings.autoSkipSeconds === seconds ? 'active' : ''}`} aria-pressed={settings.autoSkipEnabled && settings.autoSkipSeconds === seconds} onClick={() => changeDuration(seconds)} disabled={!ready || pending}>{durationLabel(seconds)}</button>)}</div>
      {!settings.autoSkipEnabled && ready && <p className="settings-description">Choose a duration to turn on auto-skip.</p>}
    </section>
    <section className="settings-section" aria-labelledby="volume-title">
      <div className="settings-section-title"><VolumeUp /><span id="volume-title">Volume</span><span className="settings-section-value">{ready ? `${displayedVolume}%` : 'Loading…'}</span></div>
      {canChangeVolume ? <><div className="volume-slider-row"><VolumeDown aria-hidden="true" /><Slider value={Math.min(settings.maxVolumePercent, displayedVolume)} min={settings.minVolumePercent} max={settings.maxVolumePercent} disabled={!ready || pending} aria-label="Ride volume" onChange={(_, value) => { dragging.current = true; setVolume(value); }} onChangeCommitted={(_, value) => commitVolume(value)} sx={{color:'#d4d4d8', '& .MuiSlider-thumb': {width:18,height:18}, '& .MuiSlider-rail': {color:'#52525b'}}} /><VolumeUp aria-hidden="true" /></div><p className="settings-description">Keep it comfortable for everyone.</p></> : <p className="settings-description">Your driver is managing the volume.</p>}
    </section>
    <p className={`settings-feedback ${error ? 'error-text' : ''}`} role={error ? 'alert' : 'status'}>{error || (pending ? 'Saving…' : notice || 'Changes apply to the music in your car.')}</p>
  </Drawer>;
}
